// Copyright (c) Microsoft. All rights reserved.

/**
 * Global HTTP interceptor service for managing authentication tokens
 */
import { azureToken } from '../../components/views/AzureToken';

export class HttpInterceptorService {
    private static instance: HttpInterceptorService;
    private teamsToken: string | null = null;
    private msalToken: string | null = null;

    private constructor() {}

    public static getInstance(): HttpInterceptorService {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!HttpInterceptorService.instance) {
            HttpInterceptorService.instance = new HttpInterceptorService();
        }
        return HttpInterceptorService.instance;
    }

    /**
     * Get Teams token by calling azureToken function
     * This is an async operation
     */
    public async getTeamsToken(scopes: string[] = ['User.Read']): Promise<string | null> {
        try {
            const token = await azureToken(scopes);
            return token || null;
        } catch (error) {
            console.error('Failed to get Teams token:', error);
            return null;
        }
    }

    /**
     * Set the Teams authentication token
     */
    public setTeamsToken(token: string | null): void {
        this.teamsToken = token;
        // Set token to request headers
        ({
            Authorization: `Bearer ${token}`,
            'X-Token-Source': 'Teams',
        });
        console.log(
            '🔐 Teams token updated in HttpInterceptorService:',
            token ? `${token.substring(0, 30)}...` : 'null',
        );
    }

    /**
     * Set the MSAL authentication token
     */
    public setMsalToken(token: string | null): void {
        this.msalToken = token;
        console.log(
            '🔐 MSAL token updated in HttpInterceptorService:',
            token ? `${token.substring(0, 30)}...` : 'null',
        );
    }

    /**
     * Get the current authentication token (Teams token takes priority)
     */
    public getCurrentToken(): string | null {
        return this.teamsToken ?? this.msalToken;
    }

    /**
     * Get authentication headers for API requests
     */
    public getAuthHeaders(): Record<string, string> {
        const token = this.getCurrentToken();
        if (token) {
            return {
                Authorization: `Bearer ${token}`,
                'X-Token-Source': this.teamsToken ? 'Teams' : 'MSAL',
            };
        }
        return {};
    }

    /**
     * Check if we have a valid authentication token
     */
    public hasValidToken(): boolean {
        return Boolean(this.getCurrentToken());
    }

    /**
     * Clear all tokens
     */
    public clearTokens(): void {
        this.teamsToken = null;
        this.msalToken = null;
        console.log('🧹 All tokens cleared from HttpInterceptorService');
    }

    /**
     * Get token info for debugging
     */
    public getTokenInfo(): { hasTeamsToken: boolean; hasMsalToken: boolean; currentSource: string } {
        return {
            hasTeamsToken: Boolean(this.teamsToken),
            hasMsalToken: Boolean(this.msalToken),
            currentSource: this.teamsToken ? 'Teams' : this.msalToken ? 'MSAL' : 'None',
        };
    }
}

// Export singleton instance
export const httpInterceptor = HttpInterceptorService.getInstance();
